// Web console sign-in. Primary flow: bot approval — the console mints a nonce
// (POST /api/auth/device-start), deep-links into the ops bot, and polls
// GET /api/auth/device-poll until the operator approves in Telegram. Fallback:
// the official Telegram Login Widget (POST /api/auth/telegram-widget). Both
// mint the same 7-day session token. Never reachable from initData context —
// App only mounts this outside Telegram.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type TelegramWidgetPayload } from '../lib/api';
import { errorWords } from '../lib/errors';
import { setWebSession } from '../lib/webSession';
import { StateLine } from '../components/kit';
import { failLine } from '../components/OnboardingParts';

const BOT_USERNAME = import.meta.env.VITE_TG_BOT_USERNAME ?? 'DubaiOTC_Ops_Bot';

const POLL_INTERVAL_MS = 2000;

type DevicePhase = 'idle' | 'starting' | 'waiting' | 'denied' | 'expired';

// Anti-phishing confirmation code. MUST match the derivation in
// bff/src/routes/bot.ts — the bot's approval message shows the same code, so
// the operator can tell this browser's request apart from a deep link someone
// else minted and sent to them.
function loginConfirmCode(nonce: string): string {
  return nonce.slice(0, 6).toUpperCase();
}

/** A sign-in that failed, as ONE red line centred in its card: what could not be done — its headline — and the server's first
 *  sentence when that is short; a long one is behind Details (the same rule as every failed read: components/OnboardingParts). */
function SignInFailed({ what, error }: { what: string; error: string }) {
  const { line, more } = failLine(`Couldn't ${what}`, error);
  return (
    <StateLine tone="bad" className="w-full [&>p]:justify-center" more={more}>
      {line}
    </StateLine>
  );
}

function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramWidgetPayload) => void;
  }
}

export function WebLogin({ onAuthed }: { onAuthed?: () => void }) {
  const navigate = useNavigate();

  // Bot-approval flow
  const [phase, setPhase] = useState<DevicePhase>('idle');
  const [nonce, setNonce] = useState<string | null>(null);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [deviceError, setDeviceError] = useState<string | null>(null);

  // Widget fallback
  const widgetRef = useRef<HTMLDivElement>(null);
  const [showWidget, setShowWidget] = useState(false);
  const [widgetBusy, setWidgetBusy] = useState(false);
  const [widgetError, setWidgetError] = useState<string | null>(null);

  const startDeviceLogin = async () => {
    setPhase('starting');
    setDeviceError(null);
    try {
      const res = await api.authDeviceStart();
      setNonce(res.nonce);
      setDeepLink(res.deep_link);
      setSecondsLeft(res.expires_in);
      setPhase('waiting');
      // Popup blockers may eat this (it follows an await) — the waiting card
      // keeps an explicit "Open Telegram" link as the manual path.
      window.open(res.deep_link, '_blank', 'noopener');
    } catch (e) {
      setPhase('idle');
      // The API says why. Guessing "check your connection" told the operator to
      // fix their wifi when device-start had actually answered db_error — the
      // tunnel to the Mac being down, which no amount of retrying fixes.
      setDeviceError(errorWords(e));
    }
  };

  // Poll while waiting. approved → store the token and enter the console;
  // denied/expired → terminal state with retry. Transient poll failures are
  // swallowed — the countdown is the real deadline. Interval clears on
  // unmount and whenever the phase leaves 'waiting'.
  useEffect(() => {
    if (phase !== 'waiting' || !nonce) return;
    let cancelled = false;
    let inFlight = false;
    const id = window.setInterval(async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await api.authDevicePoll(nonce);
        if (cancelled) return;
        if (res.status === 'approved' && res.token) {
          window.clearInterval(id);
          setWebSession(res.token);
          onAuthed?.();
          navigate('/', { replace: true });
        } else if (res.status === 'denied') {
          setPhase('denied');
        } else if (res.status === 'expired') {
          setPhase('expired');
        }
      } catch {
        // Keep polling — the countdown below expires the attempt.
      } finally {
        inFlight = false;
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, nonce]);

  // Countdown ticks once a second while waiting.
  useEffect(() => {
    if (phase !== 'waiting') return;
    const id = window.setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [phase]);

  // Local expiry — flipping the phase also tears down the poll interval.
  useEffect(() => {
    if (phase === 'waiting' && secondsLeft <= 0) setPhase('expired');
  }, [phase, secondsLeft]);

  // Fallback widget. The script is injected only when the fallback is revealed
  // (one-way disclosure) and must not be re-injected on re-renders.
  useEffect(() => {
    if (!showWidget) return;
    window.onTelegramAuth = async (user) => {
      setWidgetBusy(true);
      setWidgetError(null);
      try {
        const { token } = await api.authTelegramWidget(user);
        setWebSession(token);
        onAuthed?.();
        navigate('/', { replace: true });
      } catch (e) {
        setWidgetBusy(false);
        // Only a 403 means "not a registered operator", and errorWords already
        // says that. Printing it for every failure accused the operator's own
        // account whenever the network dropped or the books were down.
        setWidgetError(errorWords(e));
      }
    };

    const el = widgetRef.current;
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    el?.appendChild(script);
    return () => {
      delete window.onTelegramAuth;
      el?.replaceChildren();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWidget]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-6">
      <div className="flex flex-col items-center gap-3">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-tg-accent to-brand-gold-dim grid place-items-center text-figure font-extrabold text-tg-button-text tracking-tight select-none">
          D
        </div>
        <div className="text-center">
          <h1 className="text-title font-semibold tracking-tight">DubaiOTC console</h1>
          <p className="text-tg-hint text-body mt-0.5">Operator sign-in</p>
        </div>
      </div>

      <div className="card flex flex-col items-center gap-4 px-8 py-6 w-full max-w-xs">
        {(phase === 'idle' || phase === 'starting') && (
          <>
            <button
              type="button"
              className="btn-primary w-full"
              onClick={startDeviceLogin}
              disabled={phase === 'starting'}
            >
              {phase === 'starting' ? 'Starting…' : 'Continue in Telegram'}
            </button>
            {deviceError && <SignInFailed what="start the sign-in" error={deviceError} />}
          </>
        )}

        {phase === 'waiting' && (
          <>
            <span className="flex items-center gap-2 text-body">
              <span className="w-2 h-2 rounded-full bg-tg-accent animate-pulse" aria-hidden />
              Approve the sign-in in Telegram
            </span>
            {nonce && (
              <p className="text-title font-semibold tracking-[0.25em] tabular select-all">
                {loginConfirmCode(nonce)}
              </p>
            )}
            <p className="text-tg-hint text-meta text-center">
              Only approve if Telegram shows this code.
            </p>
            <p className="text-tg-hint text-meta tabular">Expires in {formatCountdown(secondsLeft)}</p>
            {deepLink && (
              <a className="btn-link" href={deepLink} target="_blank" rel="noreferrer">
                Open Telegram
              </a>
            )}
          </>
        )}

        {(phase === 'denied' || phase === 'expired') && (
          <>
            <StateLine tone={phase === 'denied' ? 'bad' : 'warn'} className="w-full [&>p]:justify-center">
              {phase === 'denied'
                ? 'The sign-in was denied in Telegram.'
                : 'The sign-in request expired.'}
            </StateLine>
            <button type="button" className="btn-primary w-full" onClick={startDeviceLogin}>
              Try again
            </button>
          </>
        )}
      </div>

      {showWidget ? (
        <div className="card flex flex-col items-center gap-3 px-8 py-5 w-full max-w-xs">
          <div ref={widgetRef} className={widgetBusy ? 'hidden' : 'min-h-[40px]'} />
          {widgetBusy && <p className="text-tg-hint text-body">Verifying…</p>}
          {widgetError && <SignInFailed what="verify you" error={widgetError} />}
        </div>
      ) : (
        <button type="button" className="btn-link" onClick={() => setShowWidget(true)}>
          Trouble? Use the Telegram widget
        </button>
      )}

      <p className="text-tg-hint text-meta text-center max-w-xs">
        Desk operators only — approve with your ops-bot Telegram account.
      </p>
    </div>
  );
}
